import React, { useEffect, useState, useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, LayoutGridIcon, WrenchIcon, ChevronRightIcon, ChevronLeftIcon } from 'lucide-react';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider';
interface ProjectDetailProps {
  title: string;
  gridLineColor?: string;
}
export function WebMessagingAdsGrowth({
  title,
  gridLineColor = '#EEE8E2'
}: ProjectDetailProps) {
  const scrollRef1 = useRef<HTMLDivElement>(null);
  const scrollRef2 = useRef<HTMLDivElement>(null);
  const scrollRef3 = useRef<HTMLDivElement>(null);
  const [atEnd1, setAtEnd1] = useState(false);
  const [atEnd2, setAtEnd2] = useState(false);
  const [atEnd3, setAtEnd3] = useState(false);
  const checkScrollEnd = (el: HTMLDivElement) => {
    return el.scrollLeft + el.clientWidth >= el.scrollWidth - 10;
  };
  useEffect(() => {
    const el = scrollRef1.current;
    if (!el) return;
    const handleScroll = () => {
      setAtEnd1(checkScrollEnd(el));
    };
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const el = scrollRef2.current;
    if (!el) return;
    const handleScroll = () => {
      setAtEnd2(checkScrollEnd(el));
    };
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const el = scrollRef3.current;
    if (!el) return;
    const handleScroll = () => {
      setAtEnd3(checkScrollEnd(el));
    };
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollRight = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollBy({
        left: 380,
        behavior: 'smooth'
      });
    }
  };
  const scrollLeft = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollBy({
        left: -380,
        behavior: 'smooth'
      });
    }
  };
  // Scroll to top when mounting
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.19, 1, 0.22, 1]
      }
    }
  };
  return <motion.article variants={container} initial="hidden" animate="show" className="w-full pb-[0px]" data-id="element-431" data-mp="18l4tm9sspcr3">
      {/* Header Section */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 mb-16 md:mb-24 text-center pt-12" data-id="element-432" data-mp="zturvv7meabb">
        <motion.h1 variants={item} className="text-4xl md:text-6xl font-bold text-ink tracking-tight mb-6" data-id="element-433" data-mp="1njoz9x9676o6">
          {title}
        </motion.h1>
        <motion.p variants={item} className="text-xl md:text-2xl text-stone-medium font-light leading-relaxed max-w-3xl mx-auto mb-16" data-id="element-434" data-mp="1sci8oyfl336l">
          Transformed a fragmented web-to-messaging ads ecosystem into a unified
          strategy, aligning teams around shared conversion gaps and scalable
          solutions.
        </motion.p>

        {/* Metadata Row */}
        <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left border-t border-b border-stone-border py-8" data-id="element-435" data-mp="26gpghyubt6gi">
          <div data-id="element-436" data-mp="1eq2yaz499x59">
            <h3 className="text-sm font-bold text-ink mb-2" data-id="element-437" data-mp="1eqcgi0j2700e">Problem</h3>
            <p className="text-stone-medium text-sm" data-id="element-438" data-mp="1spkfasbb8642">
              Fewer conversion across web-to-messaging journeys
            </p>
          </div>
          <div data-id="element-439" data-mp="1nrsok1meiq1g">
            <h3 className="text-sm font-bold text-ink mb-2" data-id="element-440" data-mp="343poq0ual86s">Approach</h3>
            <p className="text-stone-medium text-sm" data-id="element-441" data-mp="345smj71os5m2">
              System-level audit to align teams around shared experience gaps
            </p>
          </div>
          <div data-id="element-442" data-mp="18oj4hec8oagh">
            <h3 className="text-sm font-bold text-ink mb-2" data-id="element-443" data-mp="18p2dg5jwvoid">Impact</h3>
            <p className="text-stone-medium text-sm" data-id="element-444" data-mp="1dlst4rrt0i39">
              Clearer strategy and a scalable foundation for conversion
              growth{' '}
            </p>
          </div>
        </motion.div>
      </div>

      {/* Hero Image */}
      <motion.div variants={item} className="w-full mb-16 md:mb-24 bg-[#FAF6F0]" style={{
      backgroundImage: `linear-gradient(${gridLineColor} 1px, transparent 1px), linear-gradient(90deg, ${gridLineColor} 1px, transparent 1px)`,
      backgroundSize: '21px 21px'
    }} data-id="element-445" data-mp="1molg4itye7na">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-0 pt-[8px]" data-id="element-446" data-mp="26b9rs760ov7x">
          <div className="w-full aspect-[16/9] md:aspect-[21/9] rounded-t-2xl overflow-hidden relative flex items-center justify-center" data-id="element-447" data-mp="26agfkqoahdie">
            <img src="https://cdn.magicpatterns.com/uploads/og49rv9jvJxuyDMG7DnaNJ/WTX_hero.png" alt="Web Messaging Ads Growth Hero" className="max-w-full max-h-full object-contain" data-id="element-448" data-mp="2fbodh7mw8cby" />
          </div>
        </div>
      </motion.div>

      {/* Paragraph Only Modules */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 mb-24 space-y-16" data-id="element-449" data-mp="3qsiokz51qccl">
        <motion.section variants={item} data-id="element-450" data-mp="3luh2s6g8jyxf">
          <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-451" data-mp="3lvipx9aj0bvi">Context</h2>
          <p className="text-stone-medium leading-relaxed mb-6" data-id="element-452" data-mp="mflruebzr312">
            Website to Messaging Ads enables businesses to add messaging entry
            points (Messenger, WhatsApp, Instagram Direct) to their websites,
            allowing customers to either complete purchases or start
            conversations before buying. Since launching in 2023, the product
            grew quickly into a key revenue driver spanning multiple teams,
            platforms, and advertiser workflows.
          </p>
        </motion.section>

        <motion.section variants={item} data-id="element-453" data-mp="11as0l92h1fy3">
          <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-454" data-mp="1k8lefisvgouu">Challenge</h2>
          <p className="text-stone-medium leading-relaxed mb-6" data-id="element-455" data-mp="1k9d9msik4kkl">
            Despite a shared goal of driving conversions, the experience was
            fragmented across surfaces and messaging platforms, leading to
            suboptimal user journeys and lower conversions. Conversations were
            also handled mostly through manual advertiser responses, limiting
            scalability. This created an opportunity to introduce AI automation
            and design a more unified, scalable messaging ads ecosystem.
          </p>
          <ul className="list-disc pl-5 text-stone-medium space-y-2" data-id="element-456" data-mp="shxgefmhvzle"></ul>
          <div className="flex flex-col gap-3 mt-6" data-id="element-457" data-mp="sjs421p5i5t6">
            <div className="w-full rounded-md border border-stone-border overflow-hidden bg-stone-light flex items-center justify-center" data-id="element-458" data-mp="wjsg5fos24x2">
              <video src="https://cdn.magicpatterns.com/uploads/43A4js3abimgrUSfmA5RkD/product%20overview.mp4" autoPlay loop muted playsInline className="w-full h-auto object-contain" data-id="element-459" data-mp="2mtlygqo0wia0" />
            </div>
            <p className="text-sm text-stone-medium font-medium" data-id="element-460" data-mp="ozhvphz99jqa">
              Current web messaging ads expereince
            </p>
          </div>
        </motion.section>
      </div>

      {/* Approach Section (3 Pillars) */}
      <motion.section variants={item} className="max-w-5xl mx-auto px-6 md:px-12 mb-32 text-center" data-id="element-461" data-mp="ywwc5hl6i8il">
        <h2 className="text-3xl font-bold text-ink mb-16" data-id="element-462" data-mp="txt73x5782am">Approach</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-463" data-mp="17ym20izewze9">
          <div className="flex flex-col items-center" data-id="element-464" data-mp="17vzrqbl505sc">
            <div className="w-16 h-16 rounded-full bg-stone-light flex items-center justify-center mb-6" data-id="element-465" data-mp="130khdtmc8s0o">
              <MapPinIcon className="w-6 h-6 text-ink" data-id="element-466" data-mp="39q8vcr6s16dw" />
            </div>
            <h3 className="font-bold text-ink mb-2" data-id="element-467" data-mp="34rfecosa6x8z">Ecosystem audit</h3>
            <p className="text-sm text-stone-medium" data-id="element-468" data-mp="2jv7weahad4iq">
              Audited the end-to-end web-to-messaging ecosystem
            </p>
          </div>
          <div className="flex flex-col items-center" data-id="element-469" data-mp="2nv08gx5w1ssu">
            <div className="w-16 h-16 rounded-full bg-stone-light flex items-center justify-center mb-6" data-id="element-470" data-mp="2snjixl9a47tf">
              <LayoutGridIcon className="w-6 h-6 text-ink" data-id="element-471" data-mp="2xmw6y9bvzkxl" />
            </div>
            <h3 className="font-bold text-ink mb-2" data-id="element-472" data-mp="20w3rndws1wwv">Synthesis & Framing</h3>
            <p className="text-sm text-stone-medium" data-id="element-473" data-mp="20y6qmnfwr0dq">
              Synthesized data and UX research to identify shared experience
              gaps
            </p>
          </div>
          <div className="flex flex-col items-center" data-id="element-474" data-mp="29v7b4izuwebw">
            <div className="w-16 h-16 rounded-full bg-stone-light flex items-center justify-center mb-6" data-id="element-475" data-mp="2ewehcdmcp5gg">
              <WrenchIcon className="w-6 h-6 text-ink" data-id="element-476" data-mp="3lcgvd7qt7ae9" />
            </div>
            <h3 className="font-bold text-ink mb-2" data-id="element-477" data-mp="3qb1row2xx9xf">Solution ideation</h3>
            <p className="text-sm text-stone-medium" data-id="element-478" data-mp="3q97a9rqvnzy3">
              Framed problems and aligned teams on scalable solution directions
            </p>
          </div>
        </div>
      </motion.section>

      {/* Paragraph with Image Modules */}

      {/* Full Bleed Image Module - Updated */}
      <motion.section variants={item} className="w-full bg-[#f5f5f5] py-24 pb-[48px] mb-[128px]" data-id="element-479" data-mp="14w2cwerycz41">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center mb-[32px]" data-id="element-480" data-mp="zwy1dhhk2paj">
          <h2 className="text-3xl font-bold text-ink mb-12" data-id="element-481" data-mp="vyi5sp7es8li">
            Funnel and UX insight
          </h2>
          <img src="https://cdn.magicpatterns.com/uploads/wJNsqKejaT83dkiq9cZjhd/Funnel_insight.png" alt="Funnel and UX insight diagram" className="w-[120%] mx-auto object-contain pt-[8px] pb-[8px] mt-[0px] mb-[0px]" data-id="element-482" data-mp="r06wcfl1003t" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-483" data-mp="2gcim3qcwyjig">
          <div className="relative" data-id="element-484" data-mp="2bdfmilfpd5hv">
            <div ref={scrollRef3} className="flex overflow-x-auto gap-5 pb-6 snap-x snap-mandatory hide-scrollbar items-center justify-start" data-id="element-485" data-mp="2beh478r84q8k">
              {/* Tile 1 */}
              <div className="flex-none w-[400px] sm:w-[480px] snap-start bg-white p-8 rounded-sm flex items-center gap-8 min-h-[160px]" data-id="element-486" data-mp="1payoxejde8ut">
                <div className="flex-1 min-w-0" data-id="element-487" data-mp="1z6iivvedixgx">
                  <h3 className="text-[16px] font-bold text-ink mb-2" data-id="element-488" data-mp="1z7tjjmcivp3t">
                    Low consumer message
                  </h3>
                  <p className="text-stone-medium text-sm leading-relaxed" data-id="element-489" data-mp="16imnqv4gddm9">
                    Consumers feel uncertain about how to begin the
                    conversation.
                  </p>
                </div>
                <div className="flex-shrink-0 text-right" data-id="element-490" data-mp="1ggaihl226l4f">
                  <div className="text-4xl font-bold text-ink" data-id="element-491" data-mp="1gepopxnkj67m">2.4%</div>
                  <div className="text-xs text-stone-medium mt-1" data-id="element-492" data-mp="spew53gdgi4k">
                    Click rate
                  </div>
                </div>
              </div>

              {/* Tile 2 */}
              <div className="flex-none w-[400px] sm:w-[480px] snap-start bg-white p-8 rounded-sm flex items-center gap-8 min-h-[160px]" data-id="element-493" data-mp="2zdnlgjd8q5y0">
                <div className="flex-1 min-w-0" data-id="element-494" data-mp="2ph2fnvtecqt4">
                  <h3 className="text-[16px] font-bold text-ink mb-2" data-id="element-495" data-mp="2pidintf0puj6">
                    Shallow conversation depth
                  </h3>
                  <p className="text-stone-medium text-sm leading-relaxed" data-id="element-496" data-mp="2udsu6epjzecf">
                    Business reply rates are roughly 10% lower than benchmark.
                  </p>
                </div>
                <div className="flex-shrink-0" data-id="element-497" data-mp="3n2gm2t4t51yy">
                  <div className="w-20 h-16 flex items-end justify-center gap-1.5 p-3 rounded-sm bg-[#Ffffff]" data-id="element-498" data-mp="39404261hvp1i">
                    <div className="w-3 h-[35%] bg-[#D6D3D1]" data-id="element-499" data-mp="3d5bu2coa6ca8"></div>
                    <div className="w-3 bg-stone-400 h-[55%]" data-id="element-500" data-mp="1xpd32e6sowx7"></div>
                    <div className="w-3 h-[70%] bg-[#E5E5E5]" data-id="element-501" data-mp="1xs9102in7lln"></div>
                    <div className="w-3 bg-stone-500 h-[90%]" data-id="element-502" data-mp="21t1cz3iwg4lm"></div>
                  </div>
                </div>
              </div>

              {/* Tile 3 */}
              <div className="flex-none w-[400px] sm:w-[480px] snap-start bg-white p-8 rounded-sm flex items-center gap-8 min-h-[160px]" data-id="element-503" data-mp="394o9d1427sv">
                <div className="flex-1 min-w-0" data-id="element-504" data-mp="3umr82v9z9v7u">
                  <h3 className="text-[16px] font-bold text-ink mb-2" data-id="element-505" data-mp="3ppikgf628yz7">
                    Disrupted shopping flow
                  </h3>
                  <p className="text-stone-medium text-sm leading-relaxed" data-id="element-506" data-mp="3lof65x3nvb03">
                    Limited pathways back to web and friction from app switching
                  </p>
                </div>
                <div className="flex-shrink-0 text-right" data-id="element-507" data-mp="3bq97dod5ikzg">
                  <div className="text-4xl font-bold text-ink" data-id="element-508" data-mp="36qmtrdtev2q4">5%</div>
                  <div className="text-xs text-stone-medium mt-1" data-id="element-509" data-mp="36shd4np1ams3">
                    Less conversion
                  </div>
                </div>
              </div>

              {/* Tile 4 */}
              <div className="flex-none w-[400px] sm:w-[480px] snap-start bg-white p-8 rounded-sm flex items-center gap-8 min-h-[160px]" data-id="element-510" data-mp="14wx8uh58wujn">
                <div className="flex-1 min-w-0" data-id="element-511" data-mp="1hh3yghf7bd7k">
                  <h3 className="text-[16px] font-bold text-ink mb-2" data-id="element-512" data-mp="1hfsuohlra5gg">
                    Low advertiser adoption
                  </h3>
                  <p className="text-stone-medium text-sm leading-relaxed" data-id="element-513" data-mp="1r75pni2upbqp">
                    Strict criteria and lack of targeted outreach are limiting
                    broader advertiser
                  </p>
                </div>
                <div className="flex-shrink-0 text-right" data-id="element-514" data-mp="yjjd3kbnawrp">
                  <div className="text-4xl font-bold text-ink" data-id="element-515" data-mp="13i4brfb918e1">~10%</div>
                  <div className="text-xs text-stone-medium mt-1" data-id="element-516" data-mp="13k7coudxwlxj">Adoption</div>
                </div>
              </div>
            </div>

            <button onClick={() => atEnd3 ? scrollLeft(scrollRef3) : scrollRight(scrollRef3)} className="absolute right-0 top-[calc(50%-20px)] translate-x-1/2 w-12 h-12 rounded-full border border-stone-border bg-white flex items-center justify-center shadow-sm hover:shadow-md transition-shadow cursor-pointer z-10" aria-label={atEnd3 ? 'Scroll left' : 'Scroll right'} data-id="element-517" data-mp="2exjburyr8gdd">
              {atEnd3 ? <ChevronLeftIcon className="w-6 h-6 text-ink" data-id="element-518" data-mp="piadwyw5y9c" /> : <ChevronRightIcon className="w-6 h-6 text-ink" data-id="element-519" data-mp="fmj60bk7edd3" />}
            </button>
          </div>
        </div>
      </motion.section>

      {/* Theme Section — Centered Text + Full Bleed 2-Column Images */}
      <motion.section variants={item} className="mb-32" data-id="element-520" data-mp="ofcm2b7h5gs0">
        {/* Centered text block */}
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center mb-16" data-id="element-521" data-mp="kcpri2jf5hr4">
          <h2 className="text-3xl font-bold text-ink mb-2" data-id="element-522" data-mp="kflsy0uh6okb">
            Prioritized opportunities
          </h2>

          <p className="text-stone-medium leading-relaxed max-w-2xl mx-auto" data-id="element-523" data-mp="te61waa8hjvc">
            I led cross-team workshops to identify solution opportunities and
            define themes to address key conversion gaps across the messaging
            journey.
          </p>
        </div>

        {/* Text + Image Section */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-16" data-id="element-524" data-mp="132bbk0mru143">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-525" data-mp="180mmyfspsjof">
            <div className="lg:col-span-4" data-id="element-526" data-mp="182pnhtrhtv7e">
              <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-527" data-mp="u8onyxspw45z">
                Theme 1 — Contextual prompts
              </h2>
              <p className="text-stone-medium leading-relaxed" data-id="element-528" data-mp="z0q1so5ja2y4">
                Consumers often hesitated to start a conversation when the CTA
                was simply "Message." We introduced contextual prompts powered
                by automation and ML to guide message initiation, reduce
                hesitation, and improve early conversion intent.
              </p>
            </div>
            <div className="lg:col-span-8 w-full" data-id="element-529" data-mp="fanueafba4f0">
              <div className="w-full rounded-md overflow-hidden flex justify-center" data-id="element-530" data-mp="2hyme4esav3u4">
                <img src="https://cdn.magicpatterns.com/uploads/hzcR6nVjJt1qsmoLcz4SBA/WTX_button.jpg" alt="Unified Guidance Platform onboarding" className="w-[60%] h-auto" data-id="element-531" data-mp="2i0foa424g66v" />
              </div>
            </div>
          </div>
        </div>

        {/* Full bleed 2-column image grid */}
        <div className="w-full mt-[128px]" data-id="element-532" data-mp="30w5r1thvp6lt">
          <div className="grid grid-cols-1 md:grid-cols-3 max-w-[100%] mx-auto gap-[12px]" data-id="element-533" data-mp="30v4bbbpx3vxr">
            <div className="flex flex-col" data-id="element-534" data-mp="2vwkqxfqz2s7i">
              <div className="w-full flex items-center justify-center" data-id="element-535" data-mp="2qyj5io62qgsv">
                <video src="https://cdn.magicpatterns.com/uploads/mVRE97Gb2gNT4HNFF7c1p9/Dynamic CTA.mp4" autoPlay loop muted playsInline className="w-full h-auto object-contain pl-[4px] pr-[4px]" data-id="element-536" data-mp="2mxzapr8z3p9w" />
              </div>
              <p className="text-sm text-stone-medium font-medium text-center py-4 px-6" data-id="element-537" data-mp="3n770yslisesj">
                Dynamic CTA based on context
              </p>
            </div>
            <div className="flex flex-col" data-id="element-538" data-mp="o8381zri61">
              <div className="w-full flex items-center justify-center" data-id="element-539" data-mp="3w59a5vfeqdqw">
                <video src="https://cdn.magicpatterns.com/uploads/nGT4jvpeZXSWx8tEFYb2xL/Composer.mp4" autoPlay loop muted playsInline className="w-full h-auto object-contain pl-[4px] pr-[4px]" data-id="element-540" data-mp="4sy3v4psfcg8" />
              </div>
              <p className="text-sm text-stone-medium font-medium text-center py-4 px-6" data-id="element-541" data-mp="ipl1l9pke6n7">
                FAQ (Based on Gen AI)
              </p>
            </div>
            <div className="flex flex-col" data-id="element-542" data-mp="2kkca1abxmtsa">
              <div className="w-full flex items-center justify-center" data-id="element-543" data-mp="2klf0jh5ceu7o">
                <video src="https://cdn.magicpatterns.com/uploads/5uX3vckURjWEtGrHnNuwu4/contextual propmt.mp4" autoPlay loop muted playsInline className="w-full h-auto object-contain pl-[4px] pr-[4px]" data-id="element-544" data-mp="2pidt3v9cyt3r" />
              </div>
              <p className="text-sm text-stone-medium font-medium text-center py-4 px-6" data-id="element-545" data-mp="100hyisrib28g">
                AI-powered prompt
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Theme Section — Duplicated */}
      <motion.section variants={item} className="mb-32" data-id="element-546" data-mp="8bn8b87hb38l">
        {/* Centered text block */}
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center mb-16" data-id="element-547" data-mp="mb6e3ucti2hq">
          <p className="text-2xl text-ink mb-6 mt-[32px] font-[700]" data-id="element-548" data-mp="h9h0q824hcqh">
            Theme 2 - Automated responses
          </p>
          <p className="text-stone-medium leading-relaxed max-w-2xl mx-auto" data-id="element-549" data-mp="1xmxgrwrrc3x2">
            Leverage automation or AI to handle high-frequency questions,
            lowering friction and enabling scalable conversations.
          </p>
        </div>

        {/* 2-column image grid (1:2 ratio) */}
        <div className="w-full" data-id="element-550" data-mp="3fnyczbyrr54f">
          <div className="w-full" data-id="element-551" data-mp="31mm0xbmqbnnt">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-[12px] items-stretch" data-id="element-552" data-mp="31nno2eh0s0lw">
              {/* Left column (1 part) */}
              <div className="md:col-span-4 flex flex-col" data-id="element-553" data-mp="36lyz2sj1wh5p">
                <div className="w-full h-full bg-[#F0F5EF] flex py-16 px-12 pt-[0px] pb-[0px] pl-[12px] pr-[12px] items-center justify-center" data-id="element-554" data-mp="3ub1dzsmavvwi">
                  <video src="https://cdn.magicpatterns.com/uploads/oLbXwj5tt8NPnUL3yxyMk4/Response.mp4" autoPlay loop muted playsInline className="w-full h-auto object-contain rounded-md shadow-sm" data-id="element-555" data-mp="29lbayxo4lw2" />
                </div>
                <p className="text-sm text-stone-medium font-medium text-center py-4 px-6" data-id="element-556" data-mp="lwjb05b7v6jr">
                  Ads AI instant responses
                </p>
              </div>
              {/* Right column (2 parts) */}
              <div className="md:col-span-8 flex flex-col" data-id="element-557" data-mp="173k1q6p8u935">
                <div className="w-full h-full bg-[#F0F5EF] flex items-center justify-center py-16 px-12 overflow-hidden pt-[12px] pb-[12px]" data-id="element-558" data-mp="td6uy7o390fe">
                  <img src="https://cdn.magicpatterns.com/uploads/efMAd1MDeB3CZKVgLXboZj/Chat.png" alt="Chat builder automated responses" className="w-full h-auto object-contain scale-[0.81] origin-center" data-id="element-559" data-mp="xcpasp11ylbd" />
                </div>
                <p className="text-sm text-stone-medium font-medium text-center py-4 px-6" data-id="element-560" data-mp="afm2kqm47jq1">
                  Leverage Gen AI or advertiser input
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Theme Section — Duplicated copy */}
      <motion.section variants={item} className="mb-32" data-id="element-561" data-mp="28smcldtzozq0">
        {/* Centered text block */}
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center mb-16" data-id="element-562" data-mp="3tbpvvojxuh0v">
          <p className="text-2xl text-ink mb-6 mt-[32px] font-[700]" data-id="element-563" data-mp="3pawb341slic7">
            Theme 3 - Improve advertiser outreach & education
          </p>
          <p className="text-stone-medium leading-relaxed max-w-2xl mx-auto" data-id="element-564" data-mp="6xq2xijto8d6">
            Increase advertiser awareness and confidence through onboarding and
            contextual education.
          </p>
        </div>

        {/* Full bleed single-column image */}
        <div className="w-full bg-[#F0F5EF]" data-id="element-565" data-mp="bugdxrgrsdrq">
          <div className="max-w-[50%] mx-auto" data-id="element-566" data-mp="puhgrcglhv9u">
            <div className="flex flex-col" data-id="element-567" data-mp="pu7p7kfwjg21">
              <div className="w-full flex items-center justify-center" data-id="element-568" data-mp="kvmwe62zbynm">
                <video src="https://cdn.magicpatterns.com/uploads/pTwZGix3hzmvyhRFvQkwQP/Scroll trigger.mp4" autoPlay loop muted playsInline className="w-full h-auto object-contain pl-[4px] pr-[4px] pt-[24px] pb-[24px]" data-id="element-569" data-mp="ktjykzvl518c" />
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Full Bleed Image — Context */}
      <motion.section variants={item} className="w-full mb-32" data-id="element-570" data-mp="10h9iisxirare"></motion.section>

      {/* Duplicated — Feature 2 */}

      {/* Results Module (Dark Background) */}
      <motion.section variants={item} className="w-full bg-ink text-white py-32 px-6 md:px-12" data-id="element-571" data-mp="19j90bngcyive">
        <div className="max-w-4xl mx-auto text-center" data-id="element-572" data-mp="1egsd0ixeqnl8">
          <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-573" data-mp="1j8k4cvoe0xcf">
            Impact and outcome
          </h2>
          <p className="text-xl text-stone-medium font-light leading-relaxed mb-16" data-id="element-574" data-mp="1o7dkywyz14gt">
            Shifted from siloed fixes to portfolio-level system thinking,
            aligning teams around shared conversion goals.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-12 max-w-2xl mx-auto" data-id="element-575" data-mp="1x9d17n25w4ip">
            <div data-id="element-576" data-mp="3hqm857uwn213">
              <div className="text-5xl font-bold mb-2" data-id="element-577" data-mp="3hr5gbwur6c1x">0.01%</div>
              <div className="text-sm text-stone-medium uppercase tracking-wider" data-id="element-578" data-mp="3cqrhn5f1kegk">
                Est. Ads revenue lift
              </div>
            </div>

            <div data-id="element-579" data-mp="38rs3qvbc17cz">
              <div className="text-5xl font-bold mb-2" data-id="element-580" data-mp="33qkvkv5a265s">3</div>
              <div className="text-sm text-stone-medium uppercase tracking-wider" data-id="element-581" data-mp="33s5q4krldp3n">
                teams aligned
              </div>
            </div>
          </div>
        </div>
      </motion.section>
    </motion.article>;
}